import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { uploadShiftCommentImage as uploadShiftCommentImageRequest } from "@/features/shift/lib/shiftCommentImageUpload";
import {
	CreateShiftEventCommentRequest,
	ShiftEventRequest,
	UpdateShiftEventStatusRequest,
} from "@/features/shift/model/Shift/Shift";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";
import { ShiftQueryKeys } from "@/service/ShiftHomeQueries";

type GroupSaveClearRequest = {
	groupId: number;
};

type UpdateShiftEventStatusPayload = Pick<
	UpdateShiftEventStatusRequest,
	"shiftId" | "isHandoverEvent" | "isOutOfService"
>;

type UpdateShiftEventPayload = {
	shiftLogData: ShiftEventRequest;
	shiftEventId: number;
};

// TODO: Ustin - Ensure that we put in the right type
export const useMutateCreateShiftEventComment = (_page: unknown) => {
	void _page;
	const queryClient = useQueryClient();

	const mutationFn = (request: CreateShiftEventCommentRequest) =>
		SATOSPortal.CreateShiftEventComment(request);

	const { mutateAsync: createShiftEventComment, isPending: isSubmitting } =
		useMutation({
			mutationFn,
			onError: (error) => {
				console.error("Error creating comment:", error);
			},
			onSuccess: () => {
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shiftEvent],
				});
			},
			onSettled: () => {
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shiftEvent],
				});
			},
		});

	return { createShiftEventComment, isSubmitting };
};

export const useMutateCreateShiftEvent = (_page: unknown) => {
	void _page;
	const queryClient = useQueryClient();
	const { mutateAsync: createShiftEvent, isPending: isSubmitting } =
		useMutation({
			mutationFn: (request: ShiftEventRequest) =>
				SATOSPortal.CreateShiftEvent(request),
			onMutate: (change) => {
				queryClient.setQueryData(
					[ApplicationQueryKeys.user, ShiftQueryKeys.shift],
					change,
				);
			},
			onError: (_error, _variables, previousValues) => {
				queryClient.setQueryData(
					[ApplicationQueryKeys.user, ShiftQueryKeys.shift],
					previousValues,
				);
			},
			onSettled: () => {
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
			},
		});

	return { createShiftEvent, isSubmitting };
};

export const useMutateUpdateShiftEvent = (_page: unknown) => {
	void _page;
	const queryClient = useQueryClient();

	// TODO: Ustin - Ensure that we put in the right type
	// Define the mutation function
	const mutationFn = ({
		shiftLogData,
		shiftEventId,
	}: UpdateShiftEventPayload) => {
		// Ensure shiftEventId is set correctly in the shiftLogData or used in the request as needed
		return SATOSPortal.UpdateShiftEvent(shiftLogData, shiftEventId);
	};

	const { mutateAsync: updateShiftEvent, isPending: isUpdating } = useMutation({
		mutationFn,
		onError: (_error, _updatedEventData, previousData) => {
			// Rollback to the previous data on error
			queryClient.setQueryData(
				[ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				previousData,
			);
		},
			onSuccess: () => {
				// Invalidate queries or update cache here if necessary
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shiftEvent],
				});
			},
			onSettled: () => {
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shiftEvent],
				});
			},
		});

	// Return the mutate function and the isPending state
	return { updateShiftEvent, isUpdating };
};

export const useMutateUploadFile = () => {
	const queryClient = useQueryClient();
	const mutationFn = (file: File) => SATOSPortal.UploadShiftEventFile(file);

	const { mutate: uploadFile, isPending: isUploading } = useMutation({
		mutationFn,
		onSettled: () => {
			// Invalidate and refetch by queries
			void queryClient.invalidateQueries({ queryKey: [ShiftQueryKeys.shift] });
		},
	});

	return { uploadFile, isUploading };
};

export const useMutateUploadShiftCommentImage = () => {
	const { mutateAsync: uploadShiftCommentImage, isPending: isUploading } =
		useMutation({
			mutationFn: uploadShiftCommentImageRequest,
		});

	return { uploadShiftCommentImage, isUploading };
};

export const useMutateDeleteShiftEvent = (_page: unknown) => {
	void _page;
	const queryClient = useQueryClient();

	const { mutateAsync: deleteShiftEvent, isPending: isDeleting } = useMutation({
		mutationFn: (shiftEventId: number) =>
			SATOSPortal.DeleteShiftEvent(shiftEventId),
			onSuccess: () => {
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shiftEvent],
				});
			},
			onSettled: () => {
				void queryClient.invalidateQueries({
					queryKey: [
						ApplicationQueryKeys.user,
						ApplicationQueryKeys.user,
						ShiftQueryKeys.shift,
					],
				});
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shiftEvent],
				});
			},
		});

	return { deleteShiftEvent, isDeleting };
};

export const useMutateShiftEventsSaveClear = (_page: string) => {
	void _page;
	const queryClient = useQueryClient();

	const { mutateAsync: updateGroupSaveClear, isPending: isUpdating } =
		useMutation({
			mutationFn: (request: GroupSaveClearRequest) =>
				SATOSPortal.UpdateGroupSaveClear(request),

			onError: (error) => {
				console.error("Error creating comment:", error);
			},
			onSuccess: () => {
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
			},
			onSettled: () => {
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
			},
		});

	return { updateGroupSaveClear, isUpdating };
};

export const useMutateUpdateShiftEventStatus = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateShiftEventStatus, isPending: isUpdating } =
		useMutation({
			mutationFn: (request: UpdateShiftEventStatusPayload) =>
				SATOSPortal.UpdateShiftEventStatus(request),

			onMutate: () => {
				// Snapshot current data before the mutation
				const previousData = queryClient.getQueryData([
					ApplicationQueryKeys.user,
					ShiftQueryKeys.shift,
				]);

				queryClient.setQueryData(
					[ApplicationQueryKeys.user, ShiftQueryKeys.shift],
					{},
				);

				// Return the snapshot value for rollback on error ie prev data
				return previousData;
			},
			onError: (_error, _updatedEventData, previousData) => {
				// Rollback to the prev data on error
				queryClient.setQueryData(
					[ApplicationQueryKeys.user, ShiftQueryKeys.shift],
					previousData,
				);
			},
			onSettled: () => {
				// Invalidate and refetch relevant queries after mutation is settled
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
				});
				void queryClient.invalidateQueries({
					queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shiftEvent],
				});
			},
		});

	return { updateShiftEventStatus, isUpdating };
};
